<section class="inventur-page">
    <header class="page-header">
        <h1>Inventur</h1>
        <p>Wo bestehen Abweichungen und Handlungsbedarf?</p>

        @if (!isLoading() && kritischCount() > 0) {
        <div class="critical-alert">
            <mat-icon fontSet="material-icons-outlined">warning</mat-icon>
            <span>
                {{ kritischCount() }}
                @if (kritischCount() === 1) {
                Inventur erfordert sofortige Bearbeitung
                } @else {
                Inventuren erfordert sofortige Bearbeitung
                }
            </span>
        </div>
        }
    </header>

    <section class="summary-grid" aria-label="Inventur Übersicht">
        @if (isLoading()) {
        @for (_ of skeletonSummaryCards; track $index) {
        <div class="summary-card summary-card-skeleton">
            <span class="skeleton-line skeleton-number"></span>
            <span class="skeleton-line skeleton-label"></span>
        </div>
        }
        } @else {
        <button type="button" class="summary-card critical"
            [class.summary-card-active-critical]="isFilterActive('KRITISCH')" (click)="setFilter('KRITISCH')">
            <strong>{{ kritischCount() }}</strong>
            <span>Kritisch</span>
        </button>

        <button type="button" class="summary-card"
            [class.summary-card-active-neutral]="isFilterActive(InventoryStatus.Offen)"
            (click)="setFilter(InventoryStatus.Offen)">
            <strong>{{ offenCount() }}</strong>
            <span>Offen</span>
        </button>

        <button type="button" class="summary-card"
            [class.summary-card-active-neutral]="isFilterActive(InventoryStatus.InBearbeitung)"
            (click)="setFilter(InventoryStatus.InBearbeitung)">
            <strong>{{ inBearbeitungCount() }}</strong>
            <span>In Bearbeitung</span>
        </button>

        <button type="button" class="summary-card"
            [class.summary-card-active-neutral]="isFilterActive(InventoryStatus.Abgeschlossen)"
            (click)="setFilter(InventoryStatus.Abgeschlossen)">
            <strong>{{ abgeschlossenCount() }}</strong>
            <span>Abgeschlossen</span>
        </button>

        <!-- V2: both cards were Soll-based aggregates.
        <div class="summary-card summary-card-disabled">
            <strong>{{ formatWert(gesamtDifferenz()) }} €</strong>
            <span>Gesamtdifferenz</span>
        </div>

        @if (hoheAbweichungenCount() > 0) {
        <div class="summary-card summary-card-disabled critical">
            <strong>{{ hoheAbweichungenCount() }}</strong>
            <span>Hohe Abweichungen</span>
        </div>
        }
        -->
        }
    </section>

    @if (isLoading()) {
    <section class="toolbar toolbar-skeleton">
        <div class="filiale-trigger filiale-trigger-skeleton">
            <span class="skeleton-line skeleton-icon"></span>
            <span class="skeleton-line skeleton-select"></span>
            <span class="skeleton-line skeleton-icon"></span>
        </div>

        <div class="filter-tabs">
            @for (_ of skeletonFilterTabs; track $index) {
            <div class="filter-tab filter-tab-skeleton">
                <span class="skeleton-line skeleton-filter-label"></span>
            </div>
            }
        </div>
    </section>
    } @else {
    <section class="toolbar">
        <label class="filiale-select">
            <button type="button" class="filiale-trigger" (click)="toggleFilialeDropdown()">
                <mat-icon fontSet="material-icons-outlined">storefront</mat-icon>
                <span>{{ selectedFilialeLabel() }}</span>
                <mat-icon class="select-arrow" fontSet="material-icons-outlined">
                    {{ isFilialeDropdownOpen() ? 'expand_less' : 'expand_more' }}
                </mat-icon>
            </button>

            @if (isFilialeDropdownOpen()) {
            <div class="filiale-dropdown">
                @for (filiale of filialen(); track filiale.value) {
                <button type="button" class="filiale-option"
                    [class.filiale-option-active]="selectedFiliale() === filiale.value"
                    (click)="selectFiliale(filiale.value)">
                    {{ filiale.label }}
                </button>
                }
            </div>
            }
        </label>

        <div class="filter-tabs">
            @for (filter of statusFilters(); track filter.value) {
            <button type="button" class="filter-tab" [class.filter-tab-active]="isFilterActive(filter.value)"
                (click)="setFilter(filter.value)">
                <span>{{ filter.label }}</span>
                <strong>{{ filter.count }}</strong>
            </button>
            }
        </div>
    </section>
    }

    <section class="overview-table">
        @if (isLoading()) {
        <div class="table-header">
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
            <span class="skeleton-line skeleton-header"></span>
        </div>
        } @else if (sortedInventories().length) {
        <div class="table-header">
            <button type="button" class="sort-button" [class.sort-button-active]="isSortedBy('filiale')"
                (click)="setSort('filiale')">
                Filiale
                <mat-icon fontSet="material-icons-outlined">{{ getSortIcon('filiale') }}</mat-icon>
            </button>

            <span>Bezeichnung</span>

            <button type="button" class="sort-button" [class.sort-button-active]="isSortedBy('status')"
                (click)="setSort('status')">
                Status
                <mat-icon fontSet="material-icons-outlined">{{ getSortIcon('status') }}</mat-icon>
            </button>

            <span>Fortschritt</span>

            <!-- V2: Differenz and Abw. are Soll-based.
            <button type="button" class="sort-button numeric-head" [class.sort-button-active]="isSortedBy('differenz')"
                (click)="setSort('differenz')">
                Differenz €
                <mat-icon fontSet="material-icons-outlined">{{ getSortIcon('differenz') }}</mat-icon>
            </button>

            <span class="numeric-head">Abw.</span>
            -->

            <button type="button" class="sort-button numeric-head" [class.sort-button-active]="isSortedBy('wert')"
                (click)="setSort('wert')">
                Inv. Wert
                <mat-icon fontSet="material-icons-outlined">{{ getSortIcon('wert') }}</mat-icon>
            </button>

            <button type="button" class="sort-button numeric-head" [class.sort-button-active]="isSortedBy('restzeit')"
                (click)="setSort('restzeit')">
                Restzeit
                <mat-icon fontSet="material-icons-outlined">{{ getSortIcon('restzeit') }}</mat-icon>
            </button>

            <span>Aktion</span>
        </div>
        }

        <div class="table-body">
            @if (isLoading()) {
            @for (_ of skeletonRows; track $index) {
            <div class="table-row table-row-skeleton">
                <div class="cell">
                    <span class="skeleton-line skeleton-title"></span>
                    <span class="skeleton-line skeleton-subtitle"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-short"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-pill"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-short"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-short"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-short"></span>
                </div>

                <div class="cell">
                    <span class="skeleton-line skeleton-action"></span>
                </div>
            </div>
            }
            } @else if (!sortedInventories().length) {
            <div class="empty-state">
                Keine Inventuren für den gewählten Filter.
            </div>
            } @else {
            @for (inventur of sortedInventories(); track inventur.id) {
            <div class="table-row table-row-clickable" tabindex="0" (click)="openInventory(inventur)">
                <div class="cell filiale-cell">
                    <strong [title]="inventur.filialeName">{{ inventur.filialeName }}</strong>
                    <small>{{ inventur.filialNummer }}</small>
                </div>

                <div class="cell bezeichnung-cell">
                    <span [title]="inventur.bezeichnung">{{ inventur.bezeichnung }}</span>
                </div>

                <div class="cell status-cell">
                    <span class="status-pill" [ngClass]="getStatusClass(inventur.status)">
                        {{ getStatusLabel(inventur.status) }}
                    </span>
                </div>

                <div class="cell fortschritt-cell">
                    <span class="fortschritt-bar">
                        <span class="fortschritt-bar-fill"
                            [class.fortschritt-bar-fill-fertig]="inventur.status === InventoryStatus.Abgeschlossen"
                            [class.fortschritt-bar-fill-gering]="isFortschrittGering(inventur)"
                            [style.width.%]="getFortschrittProzent(inventur)"></span>
                    </span>

                    <small>{{ getFortschrittText(inventur) }}</small>
                </div>

                <!-- V2: Differenz and Abw. cells.
                <div class="cell numeric-cell">
                    <span [class.value-critical]="isDifferenzKritisch(inventur)">
                        {{ getDifferenzText(inventur) }}
                    </span>
                </div>

                <div class="cell numeric-cell">
                    <span [class.value-critical]="isAbweichungEskaliert(inventur)">
                        {{ getAbweichungenText(inventur) }}
                    </span>
                </div>
                -->

                <div class="cell numeric-cell wert-cell">
                    <span>{{ getInventurWertText(inventur) }}</span>
                </div>

                <div class="cell numeric-cell">
                    <span [class.value-critical]="isRestzeitKritisch(inventur)"
                        [class.value-warning]="isRestzeitWarnung(inventur)">
                        {{ getRestzeitText(inventur) }}
                    </span>
                </div>

                <div class="cell action-cell">
                    <button type="button" (click)="
                                $event.stopPropagation();
                                startInventory(inventur)
                            ">
                        {{ getActionLabel(inventur) }}
                    </button>
                </div>
            </div>
            }
            }
        </div>
    </section>

    @if (isLoading()) {
    <footer class="overview-footer overview-footer-skeleton">
        <span class="skeleton-line skeleton-footer-item footer-item-inline"></span>
        <span class="skeleton-line skeleton-footer-item footer-item-inline"></span>
        <span class="skeleton-line skeleton-footer-item footer-item-block"></span>
    </footer>
    } @else {
    <footer class="overview-footer">
        <span class="footer-item-inline">Gesamt: <strong>{{ totalCount() }}</strong></span>
        <span class="footer-item-inline">Erledigt: <strong>{{ erledigtCount() }}/{{ totalCount() }}</strong></span>
        <!-- V2: <span class="footer-item-block">Gesamtdifferenz: <strong class="footer-gesamtdifferenz">{{ formatWert(gesamtDifferenz()) }}
                €</strong></span> -->
    </footer>
    }
</section>
